import { BookOpen } from 'lucide-react'
import SeoH1 from '../components/SeoH1.tsx'

const terms = [
  {
    term: 'AI exposure tier',
    definition: 'A classification of how much a role is exposed to AI-driven automation or augmentation, such as high, medium, or low.'
  },
  {
    term: 'AI usage level',
    definition: 'How frequently and deeply a worker uses AI tools, from none to power user, which affects how much of the wage premium they capture.'
  },
  {
    term: 'API',
    definition: 'Application Programming Interface; a way software applications send data and commands to an AI model, usually billed per token or request.'
  },
  {
    term: 'Automation probability',
    definition: 'The estimated likelihood that a significant share of a role\'s tasks can be automated with current or near-future AI.'
  },
  {
    term: 'Breakeven',
    definition: 'The point in time or volume where the benefits of an investment equal its costs, so there is no net loss or gain.'
  },
  {
    term: 'CapEx',
    definition: 'Capital Expenditure; a one-time purchase of hardware, software licenses, or equipment rather than a recurring subscription fee.'
  },
  {
    term: 'COL',
    definition: 'Cost of Living; a regional index that adjusts salary expectations and AI premium estimates based on local prices and wages.'
  },
  {
    term: 'Cumulative gap',
    definition: 'The total dollar difference between an AI-upskilled career path and a status-quo path over a multi-year period.'
  },
  {
    term: 'Displacement risk',
    definition: 'The estimated probability that AI will reduce demand or wages for a role by automating a meaningful share of its tasks.'
  },
  {
    term: 'FTE',
    definition: 'Full-Time Equivalent; a measure of total work hours equal to one full-time employee, used to compare AI output to human labor.'
  },
  {
    term: 'Fine-tuning',
    definition: 'Training an existing AI model further on a specific dataset so it performs better for a narrow domain or task.'
  },
  {
    term: 'Hallucination',
    definition: 'When an AI model generates plausible-sounding but incorrect or unsupported information, which can create business risk.'
  },
  {
    term: 'Holding period',
    definition: 'The number of years an asset such as a laptop or server is expected to remain in use before resale or replacement.'
  },
  {
    term: 'IRR',
    definition: 'Internal Rate of Return; the annualized growth rate that makes the net present value of an investment equal to zero.'
  },
  {
    term: 'LLM',
    definition: 'Large Language Model; a type of AI trained on vast text data that can generate, summarize, or reason with language.'
  },
  {
    term: 'NPV',
    definition: 'Net Present Value; the sum of future cash flows discounted back to today, used to compare career paths in today\'s dollars.'
  },
  {
    term: 'OpEx',
    definition: 'Operational Expenditure; recurring costs such as subscriptions, cloud bills, or maintenance fees.'
  },
  {
    term: 'Payback period',
    definition: 'The time it takes for an investment in training or tools to recover its initial cost through savings or higher income.'
  },
  {
    term: 'Premium',
    definition: 'The extra percentage or dollar amount that workers with AI skills earn compared to similar workers without those skills.'
  },
  {
    term: 'RAG',
    definition: 'Retrieval-Augmented Generation; a technique where an AI answers questions using facts retrieved from a specific knowledge base rather than relying only on training memory.'
  },
  {
    term: 'ROI',
    definition: 'Return on Investment; a percentage that compares the net gain of an investment to its cost, often annualized over time.'
  },
  {
    term: 'SaaS',
    definition: 'Software as a Service; a cloud application sold by subscription, such as a hosted AI tool versus software you run yourself.'
  },
  {
    term: 'TCO',
    definition: 'Total Cost of Ownership; the full cost of owning and operating a solution over its lifetime, including hardware, software, and labor.'
  },
  {
    term: 'Token',
    definition: 'A unit of text that an AI model processes, typically a word fragment or small word; API costs and context limits are measured in tokens.'
  },
  {
    term: 'Wage compression',
    definition: 'The downward pressure on salaries when AI makes baseline tasks easier or cheaper, reducing the premium for non-AI-augmented workers.'
  }
]

export default function GlossaryPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
            <BookOpen className="w-6 h-6" />
          </div>
          <SeoH1 fallback="Glossary" />
        </div>
        <p className="text-slate-600 max-w-2xl">
          Plain-English definitions for the financial, technical, and AI terms used across the calculators.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {terms.map((item) => (
          <div key={item.term} className="card p-5">
            <h2 className="font-semibold text-slate-900 mb-2">{item.term}</h2>
            <p className="text-sm text-slate-700 leading-relaxed">{item.definition}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
